import { reactive } from "vue";

// The step-up prompt, held outside the component that draws it so lib/api.js can raise
// it from anywhere without every caller having to know the guard exists.
//
// The shape is deliberately a *question the API asks and waits on*: a request refused
// with `reauthentication_required` calls requestReauthentication(), the dialog goes up
// over whatever page fired it, and the answer resolves the same promise — true when the
// identity was confirmed (the API then replays the refused call), false when the reader
// backed out (the original 403 is thrown as it always was). Nothing navigates, so the
// row or form the reader pressed is still in front of them either way.
export const stepUp = reactive({
  open: false,
  // Whether a dialog is mounted to answer with. False on the auth pages and in a
  // component spec that mounts one page on its own — and a caller must never be left
  // waiting on a dialog that is never going to appear.
  available: false,
});

let waiting = [];

// Called by ReauthenticationDialog on mount; the returned function unregisters it.
export function registerReauthenticationDialog() {
  stepUp.available = true;
  return () => {
    stepUp.available = false;
    // Unmounting mid-question (a sign-out, a full route change) answers "no" rather
    // than leaving the request that asked hanging forever.
    settleReauthentication(false);
  };
}

export function requestReauthentication() {
  if (!stepUp.available) return Promise.resolve(false);

  stepUp.open = true;
  return new Promise((resolve) => waiting.push(resolve));
}

// One confirmation answers every request that piled up behind it: two refused calls in
// flight at once must not mean two passwords typed.
export function settleReauthentication(confirmed) {
  stepUp.open = false;
  const resolvers = waiting;
  waiting = [];
  resolvers.forEach((resolve) => resolve(confirmed));
}
